import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface MenuButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  open?: boolean;
}

/**
 * Header control for the top wave-menu. Shows "Menu" / "Close" beside two bars
 * that cross into an X when `open`. Used as the default trigger of `WaveMenu`.
 */
export const MenuButton = forwardRef<HTMLButtonElement, MenuButtonProps>(
  ({ open = false, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-expanded={open}
      aria-label={open ? 'Close menu' : 'Open menu'}
      className={cn(
        'group/menu inline-flex items-center gap-2.5 rounded-pill border-2 border-ink px-4 py-2',
        'font-display text-sm font-semibold text-ink',
        'transition-transform duration-200 ease-bounce hover:-translate-y-0.5 active:translate-y-0',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
        className,
      )}
      {...props}
    >
      <span className="relative block h-3 w-4" aria-hidden>
        <span
          className={cn(
            'absolute left-0 block h-0.5 w-4 bg-ink transition-transform duration-300 ease-bounce',
            open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0.5',
          )}
        />
        <span
          className={cn(
            'absolute bottom-0.5 left-0 block h-0.5 w-4 bg-ink transition-transform duration-300 ease-bounce',
            open ? 'bottom-1/2 translate-y-1/2 -rotate-45' : '',
          )}
        />
      </span>
      {open ? 'Close' : 'Menu'}
    </button>
  ),
);
MenuButton.displayName = 'MenuButton';
